import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Carregando, Erro } from '../../componentes/estados';
import { useToast } from '../../componentes/toast';
import { api, get, mensagemDeErro } from '../../nucleo/api';
import { dataHora, numero, textoLivre } from '../../nucleo/formato';
import { rotuloModulo } from '../../nucleo/rotulos';
import { podeNoWorkspace, useSessao } from '../../nucleo/sessao';
import { rotuloAutor } from './Historico';
import type { EntradaAuditoria } from './tipos';
import { carregarMais } from '../../nucleo/foco';

/** Um modo declarado por módulo instalado (`GET /configuracoes/modos`). */
export interface ModoDeclarado {
  modulo: string;
  modulo_nome: string;
  chave: string;
  padrao: string;
  opcoes: string[];
  valor: string;
  definido: boolean;
  impedimentos: Record<string, { modulo: string; quantidade: number; descricao: string; chave?: string }[]>;
}

/** Texto do módulo dono (namespace dele): `<slug>:modos.<nome da chave>.<opção>.nome|descricao`. Nenhuma tela de outro módulo é importada. */
export function chaveTexto(m: Pick<ModoDeclarado, 'modulo' | 'chave'>, ...partes: string[]) {
  return `${m.modulo}:modos.${m.chave.slice(m.modulo.length + 1)}.${partes.join('.')}`;
}

/**
 * Fase 2 (W8, B §9.5): Configurações > Modo de operação. Um cartão por modo dos módulos instalados, montado da declaração
 * no `module.json`, com a frase "o que muda" de cada opção. A troca que o módulo recusa (fluxo em andamento) aparece
 * antes de salvar: a opção fica indisponível e a tela lista o que impede.
 */
export function ModosOperacao() {
  const { t } = useTranslation('configuracoes');
  const s = useSessao();
  if (!s.pode('configuracoes.editar')) {
    return (
      <Cartao titulo={t('modoOperacao.titulo')}>
        <p className="text-sm text-tinta-3">{t('modoOperacao.semPermissao')}</p>
      </Cartao>
    );
  }
  return <Lista />;
}

function Lista() {
  const { t } = useTranslation('configuracoes');
  const q = useQuery({ queryKey: ['configuracoes', 'modos'], queryFn: () => get<{ itens: ModoDeclarado[] }>('/configuracoes/modos') });
  return (
    <div className="grid min-w-0 gap-4 xl:grid-cols-2">
      <div className="flex min-w-0 flex-col gap-4">
        <p className="text-sm text-tinta-3">{t('modoOperacao.ajuda')}</p>
        {q.isPending ? (
          <Carregando />
        ) : q.isError ? (
          <Erro erro={q.error} aoTentar={() => q.refetch()} />
        ) : q.data.itens.length === 0 ? (
          <Cartao titulo={t('modoOperacao.titulo')}>
            <p className="text-sm text-tinta-3">{t('modoOperacao.vazio')}</p>
          </Cartao>
        ) : (
          q.data.itens.map((m) => <CartaoModo key={`${m.chave}:${m.valor}`} modo={m} />)
        )}
      </div>
      <HistoricoModos />
    </div>
  );
}

/**
 * O que impede a troca: com `chave`, a frase do módulo dono no idioma (`<slug>:modos.usos.<chave>`, com a quantidade);
 * sem ela, em pt a frase do servidor e nos outros idiomas o módulo e a quantidade (a regra dos erros `*.em_uso`).
 */
function textoDoUso(u: { modulo: string; quantidade: number; descricao: string; chave?: string }): string {
  const chave = u.chave ? `${u.modulo}:modos.usos.${u.chave}` : null;
  if (chave && i18n.exists(`${chave}_other`)) return i18n.t(chave, { count: u.quantidade, n: numero(u.quantidade) });
  const idioma = (i18n.resolvedLanguage ?? i18n.language ?? 'pt').slice(0, 2);
  return idioma === 'pt' ? textoLivre(u.descricao) : `${rotuloModulo(u.modulo)} (${numero(u.quantidade)})`;
}

function CartaoModo({ modo }: { modo: ModoDeclarado }) {
  const { t } = useTranslation('configuracoes');
  const { t: tg } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [escolha, setEscolha] = useState(modo.valor);
  const titulo = tg(chaveTexto(modo, 'titulo'), { defaultValue: modo.modulo_nome });
  const salvar = useMutation({
    mutationFn: () => api('PUT', '/configuracoes/modos', { modos: { [modo.chave]: escolha } }),
    onSuccess: async () => {
      await s.recarregar();
      await qc.invalidateQueries({ queryKey: ['configuracoes', 'modos'] });
      await qc.invalidateQueries({ queryKey: ['configuracoes', 'historico'] });
      toast.sucesso(t('modoOperacao.salvo'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const impedeEscolha = escolha !== modo.valor ? (modo.impedimentos[escolha] ?? []) : [];

  return (
    <Cartao titulo={titulo}>
      <div className="flex min-w-0 flex-col gap-4">
        <div role="radiogroup" aria-label={titulo} className="flex min-w-0 flex-col gap-2">
          {modo.opcoes.map((o) => {
            const impedimentos = o !== modo.valor ? (modo.impedimentos[o] ?? []) : [];
            const marcado = escolha === o;
            return (
              <button
                key={o}
                type="button"
                role="radio"
                aria-checked={marcado}
                onClick={() => setEscolha(o)}
                className={`flex min-w-0 items-start gap-3 rounded-lg border-2 px-3 py-3 text-left ${marcado ? 'border-acento-forte bg-acento-suave' : 'border-tinta-3 hover:bg-sup-2'}`}
              >
                <span className={`mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border-2 ${marcado ? 'border-acento-forte' : 'border-tinta-3'}`}>{marcado && <Check className="size-3 text-acento-forte" aria-hidden />}</span>
                <span className="flex min-w-0 flex-col gap-1">
                  <span className="flex flex-wrap items-center gap-2 text-sm font-semibold text-tinta-1">
                    {tg(chaveTexto(modo, o, 'nome'), { defaultValue: o })}
                    {o === modo.valor && <Selo cor="acento">{t('modoOperacao.atual')}</Selo>}
                    {o === modo.padrao && <span className="text-xs font-normal text-tinta-3">({t('modoOperacao.padrao')})</span>}
                  </span>
                  <span className="text-sm text-tinta-2">
                    <span className="sr-only">{t('modoOperacao.oQueMuda')}: </span>
                    {tg(chaveTexto(modo, o, 'descricao'), { defaultValue: '' })}
                  </span>
                  {impedimentos.length > 0 && <span className="text-xs text-laranja">{t('modoOperacao.impede', { lista: impedimentos.map(textoDoUso).join('; ') })}</span>}
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {/* Review r16 visual M2: um cartão por módulo, cada um com o seu salvar; secundário, para a aba nunca ter N primários. */}
          <Botao disabled={escolha === modo.valor || impedeEscolha.length > 0 || !podeNoWorkspace(s, 'configuracoes.editar')} carregando={salvar.isPending} onClick={() => salvar.mutate()}>
            {t('modoOperacao.salvar')}
          </Botao>
        </div>
      </div>
    </Cartao>
  );
}

interface Pagina<T> {
  itens: T[];
  proximo_cursor: string | null;
}

/** Trocas de modo (auditoria `configuracoes.modo`), com o nome do módulo e das opções no idioma do app. */
function HistoricoModos() {
  const { t } = useTranslation('configuracoes');
  const { t: tg } = useTranslation();
  const q = useInfiniteQuery({
    queryKey: ['configuracoes', 'historico', 'configuracoes.modo'],
    queryFn: ({ pageParam }) => get<Pagina<EntradaAuditoria>>('/configuracoes/historico', { entidade: 'configuracoes.modo', limite: 15, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (p) => p.proximo_cursor ?? undefined,
  });
  const itens = q.data?.pages.flatMap((p) => p.itens) ?? [];
  const texto = (i: EntradaAuditoria) => {
    const a = (i.antes ?? {}) as { chave?: string; modulo?: string; valor?: string };
    const d = (i.depois ?? {}) as { chave?: string; modulo?: string; valor?: string };
    const m = { modulo: d.modulo ?? a.modulo ?? '', chave: d.chave ?? a.chave ?? '' };
    if (!m.modulo || !m.chave) return t('historico.sem');
    const nome = (o?: string) => (o ? tg(chaveTexto(m, o, 'nome'), { defaultValue: o }) : '');
    return t('modoOperacao.troca', { modulo: tg(chaveTexto(m, 'titulo'), { defaultValue: m.modulo }), de: nome(a.valor), para: nome(d.valor) });
  };
  return (
    <Cartao titulo={t('modoOperacao.historico')}>
      {q.isPending ? (
        <Carregando />
      ) : q.isError ? (
        <Erro erro={q.error} aoTentar={() => q.refetch()} />
      ) : itens.length === 0 ? (
        <p className="text-sm text-tinta-3">{t('historico.vazio')}</p>
      ) : (
        <>
          <ul className="divide-y divide-borda">
            {itens.map((i) => (
              <li key={i.id} className="flex min-w-0 flex-col gap-1 py-2.5 text-sm">
                <span className="font-medium break-words">{texto(i)}</span>
                <span className="text-xs text-tinta-3">
                  {t('historico.por', { nome: rotuloAutor(i, t) })} · {dataHora(i.created_at)}
                </span>
              </li>
            ))}
          </ul>
          {q.hasNextPage && (
            <div className="flex justify-center border-t border-borda pt-3">
              <Botao variante="fantasma" carregando={q.isFetchingNextPage} onClick={(e) => carregarMais(() => q.fetchNextPage(), e.currentTarget)}>
                {tg('comum.carregarMais')}
              </Botao>
            </div>
          )}
        </>
      )}
    </Cartao>
  );
}
